import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { LuApple, LuMonitor, LuTerminalSquare, LuCopy, LuCheck } from 'react-icons/lu';
import styles from './InstallTerminal.module.scss';
import { McpTerminalCommand } from 'src/components/context/AppContextModels';

interface InstallCommand {
  os: string;
  method: string;
  command: string;
}

interface PillToggleProps {
  options: { id: string; label: React.ReactNode }[];
  selectedId: string;
  onChange: (id: string) => void;
}

const PillToggle: React.FC<PillToggleProps> = ({ options, selectedId, onChange }) => {
  const pillRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [sliderStyle, setSliderStyle] = useState<React.CSSProperties>({ width: 0, transform: 'translateX(0)' });

  useLayoutEffect(() => {
    const el = pillRefs.current[selectedId];
    if (el) {
      setSliderStyle({ width: el.offsetWidth, transform: `translateX(${el.offsetLeft}px)` });
    }
  }, [selectedId, options]);

  return (
    <div className={styles.pillGroup}>
      <div className={styles.pillSlider} style={sliderStyle} />
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          ref={(el) => {
            pillRefs.current[opt.id] = el;
          }}
          className={`${styles.pill} ${selectedId === opt.id ? styles.pillActive : ''}`}
          onClick={() => onChange(opt.id)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
};

const OS_ICON_MAP: Record<string, React.ReactNode> = {
  macOS: <LuApple size={12} />,
  Windows: <LuMonitor size={12} />,
  Linux: <LuTerminalSquare size={12} />
};

interface InstallTerminalProps {
  commands: InstallCommand[] | McpTerminalCommand[];
}

const InstallTerminal: React.FC<InstallTerminalProps> = ({ commands }) => {
  const [selectedOs, setSelectedOs] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [copied, setCopied] = useState(false);

  // Normalize McpTerminalCommand (os is array) to flat commands
  const flatCommands: InstallCommand[] = useMemo(() => {
    return (commands || []).flatMap((cmd: InstallCommand | McpTerminalCommand) => {
      if (Array.isArray((cmd as McpTerminalCommand).os)) {
        const mcpCmd = cmd as McpTerminalCommand;
        return mcpCmd.os.map((os: string) => ({ os, method: mcpCmd.method, command: mcpCmd.command }));
      }
      return [{ os: (cmd as InstallCommand).os, method: cmd.method, command: cmd.command }];
    });
  }, [commands]);

  const osOptions = useMemo(
    () =>
      [...new Set(flatCommands.map((c) => c.os))].map((os) => ({
        id: os,
        label: (
          <>
            <span className={styles.pillIcon}>{OS_ICON_MAP[os]}</span>
            {os}
          </>
        )
      })),
    [flatCommands]
  );

  const methodsForOs = useMemo(() => flatCommands.filter((c) => c.os === selectedOs), [flatCommands, selectedOs]);
  const methodOptions = useMemo(() => {
    const unique = [...new Set(methodsForOs.map((c) => c.method))];
    return unique.map((m) => ({ id: m, label: <>{m}</> }));
  }, [methodsForOs]);

  useEffect(() => {
    const ids = methodOptions.map((m) => m.id);
    if (ids.length > 0 && !ids.includes(selectedMethod)) {
      setSelectedMethod(ids[0]);
    }
  }, [methodOptions, selectedMethod]);

  // Auto-select first available OS if current selection is invalid
  useEffect(() => {
    const availableOs = osOptions.map((o) => o.id);
    if (availableOs.length > 0 && !availableOs.includes(selectedOs)) {
      setSelectedOs(availableOs[0]);
    }
  }, [osOptions, selectedOs]);

  const activeCommand = flatCommands.find((c) => c.os === selectedOs && c.method === selectedMethod)?.command || '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(activeCommand);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail if clipboard access is denied
    }
  };

  if (flatCommands.length === 0) return null;

  return (
    <div className={styles.installTerminal}>
      <div className={styles.installControls}>
        <div className={styles.installDots}>
          <span />
          <span />
          <span />
        </div>
        <PillToggle options={osOptions} selectedId={selectedOs} onChange={setSelectedOs} />
        {methodOptions.length > 1 && <PillToggle options={methodOptions} selectedId={selectedMethod} onChange={setSelectedMethod} />}
      </div>
      <div className={styles.installCommandRow}>
        <span className={styles.installPrompt}>~&gt;</span>
        <code className={styles.installCode}>{activeCommand}</code>
        <button type="button" onClick={handleCopy} className={styles.installCopyBtn} aria-label="Copy command">
          {copied ? <LuCheck size={14} color="#4caf50" /> : <LuCopy size={14} />}
        </button>
      </div>
    </div>
  );
};

export default InstallTerminal;
